/**
 * Dispatch board presentation and layout math (tracker item 40). Pure
 * functions, unit-tested in dispatchUi.test.ts.
 *
 * Times are local wall-clock "HH:MM" strings in the company timezone, as the
 * API sends them: nothing here converts timezones, and "today" always comes
 * from the server (board.today), never the browser clock.
 */

import * as types from '../api/types';
import type { IDispatchApi } from '../api';
import { getErrorCode } from '../utils/apiError';
import axios from 'axios';

export const SNAP_MINUTES = 15;
/** Vertical scale of the day grid. */
export const PIXELS_PER_MINUTE = 1.2;
/** The day axis always shows at least 06:00 to 20:00. */
export const MIN_AXIS_START = 6 * 60;
export const MIN_AXIS_END = 20 * 60;
/** Shortest card drawn, so 5-minute jobs stay clickable without covering adjacent jobs. */
export const MIN_CARD_HEIGHT = 22;

export const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TERMINAL = ['COMPLETED', 'INVOICED', 'CANCELLED'];

export function timeToMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
}

export function minutesToTime(minutes: number): string {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** "09:30" -> "9:30 AM"; null -> "No time". */
export function formatTime(value: string | null | undefined): string {
  if (!value) return 'No time';
  const minutes = timeToMinutes(value);
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const suffix = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** 75 -> "1h 15m", 45 -> "45m", 120 -> "2h". */
export function formatDuration(minutes: number | null | undefined): string {
  if (!minutes || minutes <= 0) return '0m';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** "9:00 AM - 11:00 AM" for an arrival window starting at the job's start time; '' without one. */
export function formatArrivalWindow(start: string | null | undefined, windowMinutes: number | null | undefined): string {
  if (!start || !windowMinutes) return '';
  return `${formatTime(start)} - ${formatTime(minutesToTime(timeToMinutes(start) + windowMinutes))}`;
}

/** "9:00 AM - 10:30 AM", or "No time set". */
export function formatTimeRange(start: string | null | undefined, duration: number): string {
  if (!start) return 'No time set';
  return `${formatTime(start)} - ${formatTime(minutesToTime(timeToMinutes(start) + duration))}`;
}

/** Round to the nearest snap step, kept inside the day. */
export function snapMinutes(minutes: number, step = SNAP_MINUTES): number {
  const snapped = Math.round(minutes / step) * step;
  return Math.max(0, Math.min(1440 - step, snapped));
}

export interface DayAxis {
  startMinutes: number;
  endMinutes: number;
}

/**
 * The day grid's time range: the earliest working start to the latest
 * working end among the hours given (and the company default), on whole
 * hours, never narrower than 06:00 to 20:00, and stretched to fit any timed
 * job outside it.
 */
export function dayAxis(hours: types.WorkHours[], jobs: Array<{ scheduledStartTime: string | null; durationMinutes: number }> = []): DayAxis {
  let start = MIN_AXIS_START;
  let end = MIN_AXIS_END;
  for (const h of hours) {
    start = Math.min(start, Math.floor(timeToMinutes(h.startTime) / 60) * 60);
    end = Math.max(end, Math.ceil(timeToMinutes(h.endTime) / 60) * 60);
  }
  for (const job of jobs) {
    if (!job.scheduledStartTime) continue;
    const s = timeToMinutes(job.scheduledStartTime);
    start = Math.min(start, Math.floor(s / 60) * 60);
    end = Math.max(end, Math.min(1440, Math.ceil((s + job.durationMinutes) / 60) * 60));
  }
  return { startMinutes: Math.max(0, start), endMinutes: Math.min(1440, end) };
}

/** Whole hours on the axis, for the labels. */
export function axisHours(axis: DayAxis): number[] {
  const hours: number[] = [];
  for (let m = axis.startMinutes; m < axis.endMinutes; m += 60) hours.push(m);
  return hours;
}

export function axisHeight(axis: DayAxis, ppm = PIXELS_PER_MINUTE): number {
  return (axis.endMinutes - axis.startMinutes) * ppm;
}

/** A timed card's top offset and height (height = duration, clipped to the axis). */
export function cardGeometry(startTime: string, durationMinutes: number, axis: DayAxis, ppm = PIXELS_PER_MINUTE): { top: number; height: number } {
  const start = Math.max(axis.startMinutes, timeToMinutes(startTime));
  const end = Math.min(axis.endMinutes, timeToMinutes(startTime) + durationMinutes);
  return { top: (start - axis.startMinutes) * ppm, height: Math.max(MIN_CARD_HEIGHT, (end - start) * ppm) };
}

/** The snapped start time for a drop `offsetY` pixels below the top of a day column. */
export function dropTime(offsetY: number, axis: DayAxis, ppm = PIXELS_PER_MINUTE): string {
  const raw = axis.startMinutes + offsetY / ppm;
  return minutesToTime(snapMinutes(Math.max(axis.startMinutes, Math.min(axis.endMinutes - SNAP_MINUTES, raw))));
}

export interface Lane {
  lane: number;
  lanes: number;
}

/**
 * Side-by-side layout for overlapping cards in one column: jobs that
 * (transitively) overlap share a cluster split into lanes; each job gets its
 * lane and the cluster's lane count. Touching end/start does not overlap.
 */
export function layoutLanes(jobs: Array<{ id: string; scheduledStartTime: string | null; durationMinutes: number }>): Map<string, Lane> {
  const timed = jobs
    .filter((j) => j.scheduledStartTime)
    .map((j) => ({ id: j.id, start: timeToMinutes(j.scheduledStartTime!), end: timeToMinutes(j.scheduledStartTime!) + Math.max(1, j.durationMinutes) }))
    .sort((a, b) => a.start - b.start || a.end - b.end || a.id.localeCompare(b.id));
  const result = new Map<string, Lane>();
  let cluster: Array<{ id: string; lane: number }> = [];
  let laneEnds: number[] = [];
  let clusterEnd = -1;
  const flush = () => {
    for (const entry of cluster) result.set(entry.id, { lane: entry.lane, lanes: laneEnds.length });
    cluster = [];
    laneEnds = [];
  };
  for (const job of timed) {
    if (job.start >= clusterEnd) flush();
    let lane = laneEnds.findIndex((end) => end <= job.start);
    if (lane < 0) { lane = laneEnds.length; laneEnds.push(job.end); } else laneEnds[lane] = job.end;
    cluster.push({ id: job.id, lane });
    clusterEnd = Math.max(clusterEnd, job.end);
  }
  flush();
  return result;
}

export interface ShadeBlock {
  top: number;
  height: number;
  label: string;
}

/**
 * Shaded areas of a technician's day column: outside working hours (the
 * whole column on a non-working day) and their time off.
 */
export function shadeBlocks(
  workHours: types.WorkHours,
  timeOff: types.TimeOff[],
  userId: string,
  date: string,
  axis: DayAxis,
  ppm = PIXELS_PER_MINUTE
): ShadeBlock[] {
  const blocks: ShadeBlock[] = [];
  const full = { top: 0, height: axisHeight(axis, ppm) };
  const block = (from: number, to: number, label: string) => {
    const a = Math.max(axis.startMinutes, from);
    const b = Math.min(axis.endMinutes, to);
    if (b > a) blocks.push({ top: (a - axis.startMinutes) * ppm, height: (b - a) * ppm, label });
  };
  if (!workHours.workDays.includes(weekdayOf(date))) {
    blocks.push({ ...full, label: 'Not a working day' });
  } else {
    block(axis.startMinutes, timeToMinutes(workHours.startTime), 'Outside working hours');
    block(timeToMinutes(workHours.endTime), axis.endMinutes, 'Outside working hours');
  }
  for (const off of timeOff) {
    if (off.userId !== userId || date < off.startDate || date > off.endDate) continue;
    const label = off.reason ? `Time off: ${off.reason}` : 'Time off';
    if (!off.startTime || !off.endTime) blocks.push({ ...full, label });
    else block(timeToMinutes(off.startTime), timeToMinutes(off.endTime), label);
  }
  return blocks;
}

/** Whole-day time off for a user on a date (for list views and the week grid). */
export function timeOffOn(timeOff: types.TimeOff[], userId: string, date: string): types.TimeOff[] {
  return timeOff.filter((t) => t.userId === userId && date >= t.startDate && date <= t.endDate);
}

export function weekdayOf(date: string): number {
  return new Date(`${date}T00:00:00Z`).getUTCDay();
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The 7 dates from `from` (a Monday). */
export function weekDates(from: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(from, i));
}

/** "Mon 03/03". */
export function formatDayHeading(date: string): string {
  return `${DAY_SHORT[weekdayOf(date)]} ${date.slice(5, 7)}/${date.slice(8, 10)}`;
}

/** The date one day / week before or after. */
export function shiftDate(date: string, view: types.BoardView, direction: -1 | 1): string {
  return addDays(date, (view === 'week' ? 7 : 1) * direction);
}

/**
 * Board columns / rows: TECHNICIAN users always; office staff (ADMIN,
 * OFFICE_MANAGER) when they have a job in the displayed range.
 */
export function visibleTechnicians(technicians: types.BoardTechnician[], jobs: types.BoardJob[]): types.BoardTechnician[] {
  const withJobs = new Set(jobs.map((j) => j.assignedTo).filter(Boolean));
  return technicians.filter((t) => t.role === 'TECHNICIAN' || withJobs.has(t.id));
}

/** A technician's jobs on a date, timed (by start) and untimed. */
export function jobsFor(jobs: types.BoardJob[], userId: string | null, date: string): { timed: types.BoardJob[]; untimed: types.BoardJob[] } {
  const mine = jobs.filter((j) => j.assignedTo === userId && j.scheduledDate === date);
  return {
    timed: mine.filter((j) => j.scheduledStartTime).sort((a, b) => a.scheduledStartTime!.localeCompare(b.scheduledStartTime!)),
    untimed: mine.filter((j) => !j.scheduledStartTime),
  };
}

/** Time-ordered jobs (untimed first) for lists and the day sheet. */
export function timeOrdered<T extends { scheduledStartTime: string | null; id: string }>(jobs: T[]): T[] {
  return [...jobs].sort((a, b) => {
    if (!a.scheduledStartTime && b.scheduledStartTime) return -1;
    if (a.scheduledStartTime && !b.scheduledStartTime) return 1;
    return (a.scheduledStartTime ?? '').localeCompare(b.scheduledStartTime ?? '') || a.id.localeCompare(b.id);
  });
}

export const CONFLICT_LABELS: Record<types.ScheduleConflictType, string> = {
  OVERLAP: 'Overlaps another job',
  OUTSIDE_WORKING_HOURS: 'Outside working hours',
  TIME_OFF: 'Technician is off',
  PAST_DATE: 'Date is in the past',
};

export function conflictLabel(type: string): string {
  return CONFLICT_LABELS[type as types.ScheduleConflictType] ?? type;
}

/** The confirm text before saving a change that raises warnings. */
export function conflictConfirmMessage(conflicts: types.ScheduleConflict[]): string {
  const lines = conflicts.map((c) => `- ${conflictLabel(c.type)}${c.message ? `: ${c.message}` : ''}`);
  return `This schedule has warnings:\n${lines.join('\n')}\n\nSave it anyway?`;
}

/** Status colour classes for cards (left border + background). */
export function statusClasses(status: string): string {
  switch (status) {
    case 'DRAFT': return 'border-dashed border-gray-400 bg-gray-50';
    case 'WORK_ORDER_GENERATED': return 'border-cyan-500 bg-cyan-50';
    case 'SCHEDULED': return 'border-blue-500 bg-blue-50';
    case 'IN_PROGRESS': return 'border-amber-500 bg-amber-50';
    case 'COMPLETED': return 'border-green-600 bg-green-50 opacity-70';
    case 'INVOICED': return 'border-purple-500 bg-purple-50 opacity-70';
    case 'CANCELLED': return 'border-gray-300 bg-gray-100 opacity-60 line-through';
    default: return 'border-gray-300 bg-white';
  }
}

export function isTerminalStatus(status: string): boolean {
  return TERMINAL.includes(status);
}

/** The 409 the schedule endpoint returns when someone else changed the job first. */
export function isJobChangedError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 409 && getErrorCode(err) === 'JOB_CHANGED';
}

export const JOB_CHANGED_MESSAGE = 'Someone else changed this job. The board has been refreshed.';

/** The 409 a suggestion save returns when its slot now has a conflict (tracker item 41). */
export function isSuggestionStaleError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 409 && getErrorCode(err) === 'SUGGESTION_STALE';
}

export const SUGGESTION_STALE_MESSAGE = 'That slot was just taken. The suggestions have been refreshed.';

/** Fields of a job a schedule change starts from. */
export interface SchedulableJob {
  id: string;
  status: string;
  updatedAt: string;
  scheduledDate: string | null;
  scheduledStartTime: string | null;
  assignedTo: string | null;
  arrivalWindowMinutes: number | null;
}

/**
 * The change that clears a job back to the backlog: assignee and time
 * always; the date only for a DRAFT job (the job rules refuse clearing the
 * date of a scheduled job).
 */
export function unscheduleChange(job: Pick<SchedulableJob, 'status'>): types.ScheduleChange {
  return job.status === 'DRAFT'
    ? { scheduledDate: null, scheduledStartTime: null, assignedTo: null }
    : { scheduledStartTime: null, assignedTo: null };
}

export type SaveOutcome =
  | { status: 'saved'; result: types.ScheduleJobResult }
  | { status: 'cancelled' }
  | { status: 'changed' }
  /** A suggestion's slot now has a conflict (409 SUGGESTION_STALE, or the pre-check found one): nothing was written */
  | { status: 'stale' };

/**
 * The one save path for the dialog and drag-and-drop: an extra confirm for
 * an IN_PROGRESS job, POST /check for the warnings (confirm when any), then
 * PATCH with the job's updatedAt. A 409 JOB_CHANGED comes back as
 * 'changed' (the caller refreshes and tells the user); other errors throw.
 *
 * With `suggestion` (applying a "Suggest" slot, tracker item 41) a warning is
 * never offered for override: the slot was clean when suggested, so any
 * conflict means it went stale. The PATCH carries requireNoConflicts and the
 * origin, and both a pre-check conflict and a 409 SUGGESTION_STALE come back
 * as 'stale'.
 */
export async function saveScheduleChange(
  api: Pick<IDispatchApi, 'checkJobSchedule' | 'scheduleJob'>,
  job: SchedulableJob,
  change: types.ScheduleChange,
  confirm: (message: string) => boolean | Promise<boolean> = (message) => window.confirm(message),
  suggestion?: { origin: types.ScheduleOrigin }
): Promise<SaveOutcome> {
  if (job.status === 'IN_PROGRESS' && !(await confirm('This job is in progress. Change its schedule anyway?'))) return { status: 'cancelled' };
  try {
    const conflicts = await api.checkJobSchedule(job.id, change);
    if (conflicts.length > 0) {
      if (suggestion) return { status: 'stale' };
      if (!(await confirm(conflictConfirmMessage(conflicts)))) return { status: 'cancelled' };
    }
    const result = await api.scheduleJob(job.id, {
      expectedUpdatedAt: job.updatedAt,
      ...change,
      ...(suggestion ? { requireNoConflicts: true, origin: suggestion.origin } : {}),
    });
    return { status: 'saved', result };
  } catch (err) {
    if (isJobChangedError(err)) return { status: 'changed' };
    if (suggestion && isSuggestionStaleError(err)) return { status: 'stale' };
    throw err;
  }
}

/** Search params of the board URL (date omitted = today on the server). */
/** Board grouping (tracker item 57): technician columns (default) or the stored Job route. */
export type BoardGroup = 'technician' | 'route';

export function boardSearch(date: string | null, view: types.BoardView, group: BoardGroup = 'technician'): string {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (view !== 'day') params.set('view', view);
  if (group !== 'technician') params.set('group', group);
  const text = params.toString();
  return text ? `?${text}` : '';
}

export function dispatchPrintPath(date: string, userId?: string): string {
  const params = new URLSearchParams({ date });
  if (userId) params.set('userId', userId);
  return `/print/dispatch?${params.toString()}`;
}

/** Arrival window choices for the schedule dialog and job forms. */
export const ARRIVAL_WINDOW_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 180, label: '3 hours' },
  { value: 240, label: '4 hours' },
];

/** "Suggested: 1h 15m from lines". */
export function suggestionHint(suggested: number | null | undefined): string {
  return suggested ? `Suggested: ${formatDuration(suggested)} from lines` : '';
}

/** "8:00 AM · 1h 30m · arrive 8:00 AM - 10:00 AM" for a recurring schedule's template job; "No time set" without a time. */
export function visitTimeLabel(template: {
  scheduledStartTime?: string | null;
  estimatedDurationMinutes?: number | null;
  arrivalWindowMinutes?: number | null;
} | null | undefined): string {
  if (!template) return 'No time set';
  const parts = [template.scheduledStartTime ? formatTime(template.scheduledStartTime) : 'No time set'];
  if (template.estimatedDurationMinutes) parts.push(formatDuration(template.estimatedDurationMinutes));
  const window = formatArrivalWindow(template.scheduledStartTime, template.arrivalWindowMinutes);
  if (window) parts.push(`arrive ${window}`);
  return parts.join(' · ');
}

// ---- Route view (tracker item 57) ----

/** data-route value of the Unrouted group. */
export const UNROUTED_GROUP = '__unrouted__';

export interface RouteGroup {
  /** null = Unrouted */
  id: string | null;
  name: string;
  isActive: boolean;
}

/**
 * Rows of the Route view: active routes in board order, inactive or unknown
 * routes only while a job in range still uses them (so no job disappears),
 * and Unrouted last.
 */
export function routeGroups(board: Pick<types.DispatchBoard, 'routes' | 'jobs'>): RouteGroup[] {
  const routes = board.routes ?? [];
  const used = new Set(board.jobs.map((job) => job.routeId).filter((id): id is string => !!id));
  const known = routes
    .filter((route) => route.isActive !== false || used.has(route.id))
    .map((route) => ({ id: route.id, name: route.name, isActive: route.isActive !== false }));
  const unknown = [...used]
    .filter((id) => !routes.some((route) => route.id === id))
    .map((id) => ({ id, name: 'Unknown route', isActive: false }));
  return [...known, ...unknown, { id: null, name: 'Unrouted', isActive: true }];
}

/** A route's jobs on a date (by the stored Job route only), untimed first then by start. */
export function routeJobs(jobs: types.BoardJob[], routeId: string | null, date: string): types.BoardJob[] {
  return timeOrdered(jobs.filter((job) => (job.routeId ?? null) === routeId && job.scheduledDate === date));
}

export interface RouteCoverage {
  technician: types.BoardTechnician;
  /** true = dated extra coverage for this date; false = a usual route on one of their work days */
  dated: boolean;
}

/**
 * Who covers a route on a date. Informational only: it never limits who can
 * be scheduled. Usual routes count on the technician's work days. Dated
 * coverage comes from routeAssignments for that date (week boards) and, for
 * the board's selected date, the technician's dayRouteIds.
 */
export function routeCoverage(
  board: Pick<types.DispatchBoard, 'technicians' | 'routeAssignments' | 'date'>,
  routeId: string,
  date: string
): RouteCoverage[] {
  const weekday = weekdayOf(date);
  const datedIds = new Set((board.routeAssignments ?? [])
    .filter((assignment) => assignment.date === date && assignment.routeId === routeId)
    .map((assignment) => assignment.userId));
  return board.technicians.flatMap((technician) => {
    const dated = datedIds.has(technician.id) || (date === board.date && (technician.dayRouteIds ?? []).includes(routeId));
    const usual = (technician.usualRouteIds ?? []).includes(routeId) && technician.workHours.workDays.includes(weekday);
    return dated || usual ? [{ technician, dated: dated && !usual }] : [];
  });
}

/** "a@x.com, b@x.com (extra)" or null when nobody covers it. */
export function coverageLabel(coverage: RouteCoverage[]): string | null {
  if (coverage.length === 0) return null;
  return coverage.map((entry) => `${entry.technician.email}${entry.dated ? ' (extra)' : ''}`).join(', ');
}
